import { DurationInputArg2 } from 'moment';

// all flags here, alphabetically sorted

/** Use this prompt to add comments to new flags after implementation:
 *
 * Add a comment description for this flag, explaining how it's used in the
 * codebase. Be brief, don't link to call sites, just describe features in plain
 * English.
 */

/** Max session length in ms before forced logout. Not wired up yet. */
export const ABSOLUTE_TIMEOUT = 'absolute-timeout';

/** Prompts users to refresh when a newer deployed version is available. */
export const APP_VERSION = 'app-version';

/** Enables the AI Ask panel, chat command, and Ask actions on cards and collection views. */
export const ASK = 'ask';

/** Capture card create flow. */
export const ASK_CARD_CREATE = 'ask-card-create';

/** Lets Ask turns carry file attachments and voice input. */
export const ASK_FILE_AND_VOICE = 'ask-file-and-voice';

/** Gives Ask its own page for org-wide questions, instead of the floating panel. */
export const ASK_GLOBAL = 'ask-global';

/** Allows back-and-forth conversation in the Ask panel instead of single-turn only. */
export const ASK_MULTI_TURN = 'ask-multi-turn';

/** Shows the session history menu in the Ask panel's title bar. */
export const ASK_SESSION_HISTORY = 'ask-session-history';

/** Shows inline Summarize buttons on cards; requires Ask to also be enabled. */
export const ASK_SUMMARIES = 'ask-summaries';

/**
 * Saved-question widgets on the Ask page: the suggested and saved widgets its empty state shows,
 * and the action on an answer that saves its question as one. Stored in the browser only, with no
 * backend behind it yet, so it can stay off while the rest of the Ask page ships.
 */
export const ASK_TRIGGERS = 'ask-triggers';

/** Capture on the card update form: fills an existing card in from a photo, a document or speech. */
export const ASK_UPDATE_CAPTURE = 'ask-update-capture';

/** Shows automations UI inside card forms. Not wired up yet. */
export const AUTOMATIONS_IN_CARD_FORM = 'automations-in-card-form';

/** Shows bulk CSV upload on the invite-contacts screen. */
export const BATCH_UPLOAD_USERS = 'batch-upload-users';

/** Allows renaming internal (non-public) view templates in the builder. */
export const BUILDER_RENAME_INTERNAL_FORMS = 'builder-rename-internal-forms';

/**
 * Renders chat messages, Ask answers and activity mentions through the shared markdown
 * component the subform instructions use, rather than chat's own parser, and writes them in the
 * same library's editor with its toolbar hidden. One renderer and one editor means a message and
 * an instruction read the same and are typed the same; it also brings headings below h4,
 * horizontal rules, standard emphasis and the bold/italic/link shortcuts to chat for the first
 * time. Enter still sends: only the subform composer continues a list on it.
 */
export const CHAT_MARKDOWN_RENDERER = 'chat-markdown-renderer';

/** Allows permanently deleting workflow components in the builder. Not wired up yet. */
export const COMPONENT_DELETE = 'component-delete';

/** Shows CSV bulk import in channel settings. */
export const CSV_BULK_IMPORT = 'csv-bulk-import';

/** Refetches Apollo data after sleep/wake or when the browser comes back online. */
export const DATA_FRESHNESS = 'data-freshness';

/** Shows delete in the workspace section overflow menu. */
export const DELETE_WORKSPACE_SECTION = 'delete-workspace-section';

/** Unlocks the developer menu and internal builder field options (seven-tap to enable locally). */
export const DEVELOPER_TOOLS = 'developer-tools';

/** Shows duplicate in the workspace section overflow menu. */
export const DUPLICATE_WORKSPACE_SECTION = 'duplicate-workspace-section';

/** Lets users change a saved view's type (table, calendar, etc.) from view options. */
export const EDITABLE_VIEW_TYPE = 'editable-view-type';

/** Enables customizing hotkey bindings. While off, users get a fixed basic set. */
export const HOT_KEYS_CUSTOMIZATION = 'hot-keys-customization';

/** Logs users out after this many ms of inactivity; 0 disables. */
export const INACTIVITY_TIMEOUT = 'inactivity-timeout';

/** Uses multi-group grouping controls instead of a single group in collection views. */
export const MULTIPLE_FILTER_GROUPS = 'multi-filter-groups';

/** Base interval in ms for debounced permission refetch after subscription events. */
export const PERMISSIONS_REFETCH_TIMER = 'permissions-refetch-timer';

/** Lets a related card field be prefilled from another related card field's selection. */
export const PREFILL_RELATED_CARD_FROM_ANOTHER_RELATED_CARD =
  'prefill-related-card-from-another-related-card';

/**
 * Replaces the related card field's bespoke "only show cards matching" menu with the collection
 * view's filter menu, which can hold any number of filters - static values alongside the dynamic
 * "matches a field on this card" one the bespoke menu was limited to.
 */
export const RELATED_CARD_STATIC_FILTERS = 'related-card-static-filters';

/** Shows the required-field toggle in view template component settings. */
export const REQUIRED_FIELDS_IN_VIEW_TEMPLATE_BUILDER = 'required-fields-in-view-template-builder';

/**
 * Adds an expand button to a subform text instruction, which opens the markdown composer in a
 * modal with room to write and a preview of how the instruction will read.
 */
export const SUBFORM_INSTRUCTION_EDITOR_MODAL = 'subform-instruction-editor-modal';

/**
 * Offers text, image and video instruction items in the subform builder's add menu. Off until
 * the subform save path can write them into the view template layout, which is the next PR in
 * this stack — until then an instruction lives only as long as the screen does.
 */
export const SUBFORM_LAYOUT_CONTENT_ITEMS = 'subform-layout-content-items';

/** Offers the Subtable field type in the builder's Add a Field menu. */
export const SUBTABLES = 'subtables';

/** Shows User Groups in the main navigation menu. */
export const USER_GROUPS = 'user-groups';

/** Redesigned view template builder: layout-first with component settings in a right sidebar. */
export const VIEW_TEMPLATE_FIELD_SIDEBAR = 'view-template-field-sidebar';

export interface AppVersionFlag {
  version: string;
  frequency: DurationInputArg2; // e.g. 'day', 'week', etc.
}

const appVersionFlagDefault: AppVersionFlag = {
  version: import.meta.env.VITE_VERSION,
  frequency: 'week',
};

// set default flag state here, alphabetically sorted
export const defaultFlags = {
  [ABSOLUTE_TIMEOUT]: 0,
  [APP_VERSION]: appVersionFlagDefault,
  [ASK]: false,
  [ASK_CARD_CREATE]: false,
  [ASK_FILE_AND_VOICE]: false,
  [ASK_GLOBAL]: false,
  [ASK_MULTI_TURN]: false,
  [ASK_SESSION_HISTORY]: false,
  [ASK_SUMMARIES]: false,
  [ASK_TRIGGERS]: false,
  [ASK_UPDATE_CAPTURE]: false,
  [AUTOMATIONS_IN_CARD_FORM]: false,
  [BATCH_UPLOAD_USERS]: false,
  [BUILDER_RENAME_INTERNAL_FORMS]: false,
  [CHAT_MARKDOWN_RENDERER]: false,
  [COMPONENT_DELETE]: false,
  [CSV_BULK_IMPORT]: false,
  [DATA_FRESHNESS]: {
    enabled: false,
    logPrefix: '',
    maxRetries: 1,
    refetchThrottleMs: 1000,
    sleepDetectorIntervalMs: 1000,
    sleepDetectorTriggerRefetchMs: 1000,
  },
  [DELETE_WORKSPACE_SECTION]: false,
  [DEVELOPER_TOOLS]: false,
  [DUPLICATE_WORKSPACE_SECTION]: false,
  [EDITABLE_VIEW_TYPE]: false,
  [HOT_KEYS_CUSTOMIZATION]: false,
  [INACTIVITY_TIMEOUT]: 0,
  [MULTIPLE_FILTER_GROUPS]: false,
  [PERMISSIONS_REFETCH_TIMER]: 0,
  [PREFILL_RELATED_CARD_FROM_ANOTHER_RELATED_CARD]: false,
  [RELATED_CARD_STATIC_FILTERS]: false,
  [REQUIRED_FIELDS_IN_VIEW_TEMPLATE_BUILDER]: false,
  [SUBFORM_INSTRUCTION_EDITOR_MODAL]: false,
  [SUBFORM_LAYOUT_CONTENT_ITEMS]: false,
  [SUBTABLES]: false,
  [USER_GROUPS]: false,
  [VIEW_TEMPLATE_FIELD_SIDEBAR]: false,
};

export type Flags = typeof defaultFlags;
export type FlagName = keyof Flags;
